import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent, MouseEvent } from 'react';
import { Input } from 'antd';
import type { InputRef } from 'antd';
import CancelIcon from 'assets/svg/CancelIcon';
import ConfirmIcon from 'assets/svg/ConfirmIcon';
import PencilIcon from 'assets/svg/PencilIcon';
import {
    INLINE_EDITABLE_NAME_INPUT_CLASS,
    INLINE_EDITABLE_NAME_INPUT_ERROR_MODIFIER,
    StyledInlineEditableNameActionButton,
    StyledInlineEditableNameActions,
    StyledInlineEditableNameEditor,
    StyledInlineEditableNameError,
    StyledInlineEditableNameLabel,
} from './InlineEditableName.styles';

export type InlineEditableNameHandle = {
    cancelEditing: () => void;
};

export type InlineEditableNameProps = {
    /** Current (persisted) name shown in view mode and restored on cancel/blank. */
    value: string;
    /** Persists the new name. Reject/throw to keep the field open so the user can retry. */
    onSave: (nextValue: string) => Promise<void> | void;
    /** Returns a validation message (Scenario Manager naming rules) or null when valid. */
    validate?: (nextValue: string) => string | null;
    /** Hides the pencil icon and disables editing (e.g. read-only viewers). */
    editable?: boolean;
    /** Fired when the component enters or leaves inline edit mode. */
    onEditingChange?: (isEditing: boolean) => void;
    className?: string;
    textClassName?: string;
    ariaLabel?: string;
    titleTestId?: string;
    renameBtnTestId?: string;
    inputTestId?: string;
    confirmBtnTestId?: string;
    cancelBtnTestId?: string;
    errorTestId?: string;
};

const InlineEditableName = forwardRef<InlineEditableNameHandle, InlineEditableNameProps>(({
    value,
    onSave,
    validate,
    editable = true,
    onEditingChange,
    className,
    textClassName,
    ariaLabel = 'Rename',
    titleTestId,
    renameBtnTestId,
    inputTestId,
    confirmBtnTestId,
    cancelBtnTestId,
    errorTestId,
}, ref) => {
    const [isEditing, setIsEditing] = useState(false);
    const [draftValue, setDraftValue] = useState(value);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef<InputRef | null>(null);
    const isSavingRef = useRef(false);

    useEffect(() => {
        if (!isEditing) {
            setDraftValue(value);
        }
    }, [value, isEditing]);

    useEffect(() => {
        if (!isEditing) return undefined;
        const frame = requestAnimationFrame(() => {
            inputRef.current?.focus();
            inputRef.current?.input?.select();
        });
        return () => cancelAnimationFrame(frame);
    }, [isEditing]);

    const startEditing = useCallback(() => {
        if (!editable) return;
        setDraftValue(value);
        setError(null);
        setIsEditing(true);
        onEditingChange?.(true);
    }, [editable, onEditingChange, value]);

    const cancelEditing = useCallback(() => {
        setDraftValue(value);
        setError(null);
        setIsEditing(false);
        onEditingChange?.(false);
    }, [onEditingChange, value]);

    useImperativeHandle(ref, () => ({ cancelEditing }), [cancelEditing]);

    const commitEditing = useCallback(async () => {
        if (!isEditing || isSavingRef.current) return;

        const trimmedNext = draftValue.trim();
        const trimmedCurrent = value.trim();

        if (!trimmedNext) {
            setDraftValue(value);
            setError(validate?.('') ?? 'Scenario name is required.');
            return;
        }

        if (trimmedNext === trimmedCurrent) {
            setIsEditing(false);
            setError(null);
            onEditingChange?.(false);
            return;
        }

        const validationMessage = validate ? validate(trimmedNext) : null;
        if (validationMessage) {
            setError(validationMessage);
            return;
        }

        isSavingRef.current = true;
        setIsSaving(true);
        try {
            await onSave(trimmedNext);
            setIsEditing(false);
            setError(null);
            onEditingChange?.(false);
        } catch {
            // Parent surfaces the failure via toast; keep editing open so the user can retry.
        } finally {
            isSavingRef.current = false;
            setIsSaving(false);
        }
    }, [draftValue, isEditing, onEditingChange, onSave, validate, value]);

    const handleBlur = useCallback(() => {
        cancelEditing();
    }, [cancelEditing]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                void commitEditing();
            } else if (event.key === 'Escape') {
                event.preventDefault();
                cancelEditing();
            }
        },
        [cancelEditing, commitEditing],
    );

    const preventBlur = (event: MouseEvent) => {
        event.preventDefault();
    };

    if (!isEditing) {
        return (
            <div className={`flex items-center gap-2 ${className ?? ''}`}>
                <StyledInlineEditableNameLabel className={textClassName} data-testid={titleTestId}>
                    {value}
                </StyledInlineEditableNameLabel>
                {editable ? (
                    <button
                        type="button"
                        onClick={startEditing}
                        className="flex items-center justify-center rounded p-1 hover:bg-backgroundGreyAlt transition-colors"
                        aria-label={ariaLabel}
                        data-testid={renameBtnTestId}
                    >
                        <PencilIcon className="text-fontColor" />
                    </button>
                ) : null}
            </div>
        );
    }

    const inputClassName = error
        ? `${INLINE_EDITABLE_NAME_INPUT_CLASS} ${INLINE_EDITABLE_NAME_INPUT_ERROR_MODIFIER}`
        : INLINE_EDITABLE_NAME_INPUT_CLASS;

    return (
        <div className={`relative z-30 flex flex-col ${className ?? ''}`}>
            <StyledInlineEditableNameEditor>
                <Input
                    ref={inputRef}
                    className={inputClassName}
                    style={{
                        display: 'block',
                        width: '100%',
                        minWidth: 280,
                        height: 32,
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: '22px',
                        padding: '4px 10px',
                    }}
                    value={draftValue}
                    disabled={isSaving}
                    aria-label="Scenario name"
                    data-testid={inputTestId}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setDraftValue(event.target.value);
                        if (error) setError(null);
                    }}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                />
                <StyledInlineEditableNameActions>
                    <StyledInlineEditableNameActionButton
                        type="button"
                        className="shadow-axesDropdown"
                        aria-label="Cancel rename"
                        disabled={isSaving}
                        data-testid={cancelBtnTestId}
                        onMouseDown={preventBlur}
                        onClick={cancelEditing}
                    >
                        <CancelIcon />
                    </StyledInlineEditableNameActionButton>
                    <StyledInlineEditableNameActionButton
                        type="button"
                        className="shadow-axesDropdown"
                        aria-label="Confirm rename"
                        disabled={isSaving}
                        data-testid={confirmBtnTestId}
                        onMouseDown={preventBlur}
                        onClick={() => void commitEditing()}
                    >
                        <ConfirmIcon />
                    </StyledInlineEditableNameActionButton>
                </StyledInlineEditableNameActions>
            </StyledInlineEditableNameEditor>
            {error ? (
                <StyledInlineEditableNameError role="alert" data-testid={errorTestId}>
                    {error}
                </StyledInlineEditableNameError>
            ) : null}
        </div>
    );
});

InlineEditableName.displayName = 'InlineEditableName';

export default InlineEditableName;
